import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, Clock, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { getBackendUrl } from '../utils/getBackendUrl';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

function resolveUrl(u) {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${BACKEND_URL}${u}`;
}

function fmtWhen(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-CA', { timeZone: 'America/Toronto', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function PendingPhotos() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchPhotos = async () => {
    try {
      const { data } = await axios.get(`${API}/photos/pending`);
      setPhotos(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load pending photos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPhotos(); }, []);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/photos/${id}/approve`);
      setPhotos((p) => p.filter((x) => x.id !== id));
      toast.success('Photo approved — client can now see it');
    } catch {
      toast.error('Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    if (!rejectFor) return;
    const id = rejectFor;
    setBusyId(id);
    try {
      await axios.post(`${API}/photos/${id}/reject`, { reason: rejectReason });
      setPhotos((p) => p.filter((x) => x.id !== id));
      setRejectFor(null);
      setRejectReason('');
      toast.success('Photo rejected');
    } catch {
      toast.error('Failed to reject');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-28">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center gap-2 mb-1">
          <Clock className="w-6 h-6 text-amber-600" />
          <h1 className="text-xl font-bold text-gray-900">Pending Photo Approvals</h1>
        </div>
        <p className="text-sm text-gray-500 mb-5">Approve a photo to make it visible to the client. Rejected photos stay hidden.</p>

        {loading ? (
          <div className="text-center text-sm text-gray-500 py-12">Loading…</div>
        ) : photos.length === 0 ? (
          <div className="text-center py-20">
            <Check className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">All caught up — no photos waiting for approval.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="pending-photos-list">
            {photos.map((p) => (
              <div key={p.id} className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden" data-testid={`pending-${p.id}`}>
                <div className="bg-amber-50 border-b border-amber-100 px-3 py-1.5 text-[11px] font-bold text-amber-800 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Pending approval
                </div>
                <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
                  {p.file_path ? (
                    <img src={resolveUrl(p.file_path)} alt={p.description || 'pending'} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-10 h-10 text-gray-300" />
                  )}
                </div>
                <div className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2 text-xs">
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 truncate">{p.uploaded_by_name || 'Someone'}</p>
                      <p className="text-gray-500">{fmtWhen(p.timestamp)}</p>
                    </div>
                  </div>
                  {p.description && (
                    <p className="text-xs text-gray-700 line-clamp-3">{p.description}</p>
                  )}
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <button
                      onClick={() => approve(p.id)}
                      disabled={busyId === p.id}
                      className="py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center justify-center gap-1 disabled:bg-gray-200"
                      data-testid={`approve-${p.id}`}
                    >
                      <Check className="w-3.5 h-3.5" />
                      Approve
                    </button>
                    <button
                      onClick={() => { setRejectFor(p.id); setRejectReason(''); }}
                      disabled={busyId === p.id}
                      className="py-2 rounded-lg bg-white hover:bg-red-50 text-red-700 border border-red-200 text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"
                      data-testid={`reject-${p.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {rejectFor && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setRejectFor(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900">Reject this photo</h3>
            <p className="text-xs text-gray-500">The uploader will be notified. The photo stays hidden from the client.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason (optional) — e.g. 'Photo is blurry, please re-take'"
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
              data-testid="reject-reason-input"
              autoFocus
            />
            <div className="flex gap-2">
              <button onClick={() => setRejectFor(null)} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-bold text-gray-700">Cancel</button>
              <button onClick={reject} disabled={busyId === rejectFor} className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold" data-testid="confirm-reject-btn">
                Reject photo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
